import React, { useEffect, useState, useRef } from "react";
import TopNav from "@/components/TopNav";
import { api, apiErr, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, DollarSign, Clock, TrendingUp, CheckCircle2, XCircle, Wallet, Minus, Plus, History, ShieldCheck, ShieldAlert, Trash2, Ban, UserCheck, UserPlus, Download, FileDown, Contact } from "lucide-react";
import Tree from "react-d3-tree";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <Card data-testid={testId} className="p-6 bg-card/60 border-border/70 rounded-2xl">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <Icon className={`w-4 h-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
    </div>
    <div className={`mt-4 font-display font-bold text-3xl ${accent ? "text-primary" : ""}`}>{value}</div>
  </Card>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tree, setTree] = useState(null);
  const [settings, setSettings] = useState({ rates: [], joining_fee: 100 });
  const [ratesInput, setRatesInput] = useState("");
  const [adjustments, setAdjustments] = useState([]);
  const [kycList, setKycList] = useState([]);
  const [kycNote, setKycNote] = useState({});
  const [adminCustomers, setAdminCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null); // user obj or null
  const [adjustAction, setAdjustAction] = useState("credit");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editCust, setEditCust] = useState(null);
  const [deleteCustTarget, setDeleteCustTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveUtr, setApproveUtr] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", sponsor_code: "none", mark_paid: false });
  const treeRef = useRef(null);
  const [translate, setTranslate] = useState({ x: 300, y: 60 });

  const load = async () => {
    try {
      const [s, u, w, t, st, adj, kyc, custs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/withdrawals"),
        api.get("/admin/tree"),
        api.get("/admin/settings"),
        api.get("/admin/wallet-adjustments"),
        api.get("/admin/kyc"),
        api.get("/admin/customers"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setWithdrawals(w.data);
      setTree(t.data);
      setSettings(st.data);
      setRatesInput(st.data.rates.join(", "));
      setAdjustments(adj.data);
      setKycList(kyc.data);
      setAdminCustomers(custs.data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (treeRef.current) {
      const { width } = treeRef.current.getBoundingClientRect();
      setTranslate({ x: width / 2, y: 60 });
    }
  }, [tree]);

  const act = async (id, action) => {
    if (action === "approve") {
      const w = withdrawals.find((x) => x.id === id);
      setApproveTarget(w);
      setApproveUtr("");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/withdrawals/${id}`, { action });
      toast.success(`Withdrawal ${action}d`);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const submitApprove = async () => {
    if (!approveTarget) return;
    if (!approveUtr || approveUtr.trim().length < 6) { toast.error("Enter a valid UTR number (min 6 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/withdrawals/${approveTarget.id}`, { action: "approve", utr: approveUtr.trim() });
      toast.success(`Withdrawal approved · UTR ${approveUtr.trim()}`);
      setApproveTarget(null);
      setApproveUtr("");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    const parsed = ratesInput.split(",").map((r) => parseFloat(r.trim())).filter((r) => !isNaN(r));
    if (parsed.length === 0) { toast.error("Enter at least one rate"); return; }
    setBusy(true);
    try {
      await api.put("/admin/settings", { rates: parsed });
      toast.success("Commission rates updated");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openAdjust = (u) => {
    setAdjustTarget(u);
    setAdjustAction("credit");
    setAdjustAmount("");
    setAdjustNote("");
  };

  const submitAdjust = async () => {
    const amt = parseFloat(adjustAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${adjustTarget.id}/wallet`, {
        action: adjustAction,
        amount: amt,
        note: adjustNote || null,
      });
      toast.success(`${adjustAction === "credit" ? "Credited" : "Debited"} ₹${amt.toFixed(2)} · New balance: ₹${data.new_balance.toFixed(2)}`);
      setAdjustTarget(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reviewKyc = async (uid, action) => {
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${uid}`, { action, note: kycNote[uid] || null });
      toast.success(`KYC ${action}d`);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleUserStatus = async (u) => {
    const next = u.status === "suspended" ? "active" : "suspended";
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/status`, { status: next });
      toast.success(next === "suspended" ? `${u.name} suspended` : `${u.name} reactivated`);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.email}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveCustomer = async () => {
    if (!editCust) return;
    setBusy(true);
    try {
      await api.put(`/admin/customers/${editCust.id}`, {
        name: editCust.name,
        dob: editCust.dob,
        mobile: editCust.mobile,
        aadhaar: editCust.aadhaar,
        pan: editCust.pan.toUpperCase(),
        father_name: editCust.father_name || "",
        address: editCust.address || "",
        pin_code: editCust.pin_code || "",
        bank_account_number: editCust.bank_account_number || "",
        bank_ifsc: (editCust.bank_ifsc || "").toUpperCase(),
      });
      toast.success("Customer updated");
      setEditCust(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!deleteCustTarget) return;
    setBusy(true);
    try {
      await api.delete(`/admin/customers/${deleteCustTarget.id}`);
      toast.success(`Deleted customer ${deleteCustTarget.name}`);
      setDeleteCustTarget(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Name, email and password are required");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        name: newUser.name.trim(),
        email: newUser.email.trim().toLowerCase(),
        password: newUser.password,
        sponsor_code: newUser.sponsor_code === "none" ? null : newUser.sponsor_code,
        mark_paid: !!newUser.mark_paid,
      });
      toast.success(`Created ${data.email} · ref ${data.referral_code}`);
      setAddOpen(false);
      setNewUser({ name: "", email: "", password: "", sponsor_code: "none", mark_paid: false });
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!stats) return <div className="min-h-screen"><TopNav /><div className="p-8 text-muted-foreground">Loading admin panel...</div></div>;

  return (
    <div className="min-h-screen">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10 fade-up">
        <div className="mb-8">
          <div className="tag mb-3"><span className="w-1.5 h-1.5 rounded-full bg-accent" /> Admin Control</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl">Network Command Center</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <Stat testId="admin-stat-users" icon={Users} label="Total Users" value={stats.total_users} />
          <Stat testId="admin-stat-paid" icon={CheckCircle2} label="Paid Members" value={stats.paid_users} accent />
          <Stat testId="admin-stat-revenue" icon={DollarSign} label="Revenue" value={`₹${stats.total_revenue.toFixed(0)}`} accent />
          <Stat testId="admin-stat-pending" icon={Clock} label="Pending Withdrawals" value={stats.pending_withdrawals} />
          <Stat testId="admin-stat-withdrawn" icon={TrendingUp} label="Total Withdrawn" value={`₹${stats.total_withdrawn.toFixed(0)}`} />
        </div>

        <Tabs defaultValue="withdrawals" className="w-full">
          <TabsList data-testid="admin-tabs" className="bg-card/60 border border-border/70 rounded-full h-11 p-1 flex-wrap">
            <TabsTrigger data-testid="admin-tab-withdrawals" value="withdrawals" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Withdrawals</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Users</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-adjustments" value="adjustments" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Ledger</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-kyc" value="kyc" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">KYC</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-customers" value="customers" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Customers</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-tree" value="tree" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Network Tree</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-settings" value="settings" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs uppercase tracking-[0.15em] px-4">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="withdrawals" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 text-xs uppercase tracking-[0.2em] text-muted-foreground">Withdrawal queue</div>
              <div className="divide-y divide-border/40">
                {withdrawals.length === 0 && <div className="p-8 text-sm text-muted-foreground">No withdrawal requests.</div>}
                {withdrawals.map((w) => (
                  <div key={w.id} data-testid={`admin-withdrawal-${w.id}`} className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{w.user_name}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{w.user_email}</div>
                      {w.utr && <div className="text-xs text-primary font-mono mt-1">UTR: {w.utr}</div>}
                    </div>
                    <div className="font-mono font-bold text-lg">₹{w.amount.toFixed(2)}</div>
                    <div className="flex flex-col items-end gap-1">
                      <div className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
                        w.status === "approved" ? "border-primary/50 text-primary bg-primary/10"
                        : w.status === "rejected" ? "border-destructive/50 text-destructive bg-destructive/10"
                        : "border-accent/50 text-accent bg-accent/10"
                      }`}>{w.status}</div>
                    </div>
                    {w.status === "pending" && (
                      <div className="flex gap-2">
                        <Button data-testid={`approve-btn-${w.id}`} size="sm" onClick={() => act(w.id, "approve")} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button data-testid={`reject-btn-${w.id}`} size="sm" variant="destructive" onClick={() => act(w.id, "reject")} disabled={busy} className="rounded-full">
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">All members ({users.length})</div>
                <Button data-testid="add-customer-btn" onClick={() => setAddOpen(true)} className="rounded-full h-9 px-4 bg-primary text-primary-foreground hover:bg-primary/90">
                  <UserPlus className="w-4 h-4 mr-1" /> Add Customer
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    <tr className="border-b border-border/40">
                      <th className="text-left p-4">Name</th>
                      <th className="text-left p-4">Email</th>
                      <th className="text-left p-4">Code</th>
                      <th className="text-left p-4">Sponsor</th>
                      <th className="text-right p-4">Wallet</th>
                      <th className="text-right p-4">Earnings</th>
                      <th className="text-center p-4">Paid</th>
                      <th className="text-center p-4">Status</th>
                      <th className="text-right p-4">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`admin-user-${u.id}`} className="hover:bg-white/[0.02]">
                        <td className="p-4 font-medium">{u.name}</td>
                        <td className="p-4 text-muted-foreground font-mono text-xs">{u.email}</td>
                        <td className="p-4 font-mono text-primary text-xs">{u.referral_code}</td>
                        <td className="p-4 font-mono text-xs">{u.referred_by_code || "—"}</td>
                        <td className="p-4 text-right font-mono">₹{(u.wallet_balance || 0).toFixed(2)}</td>
                        <td className="p-4 text-right font-mono">₹{(u.total_earnings || 0).toFixed(2)}</td>
                        <td className="p-4 text-center">{u.joining_fee_paid ? <CheckCircle2 className="w-4 h-4 text-primary inline" /> : <XCircle className="w-4 h-4 text-muted-foreground inline" />}</td>
                        <td className="p-4 text-center">
                          <span className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
                            u.status === "suspended" ? "border-destructive/50 text-destructive bg-destructive/10" : "border-primary/50 text-primary bg-primary/10"
                          }`}>{u.status === "suspended" ? "suspended" : "active"}</span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            <Button data-testid={`adjust-wallet-btn-${u.id}`} size="sm" variant="outline" onClick={() => openAdjust(u)} className="rounded-full border-primary/40 text-primary hover:bg-primary/10 hover:text-primary h-8 px-3">
                              <Wallet className="w-3.5 h-3.5 mr-1" /> Adjust
                            </Button>
                            <Button
                              data-testid={`toggle-status-btn-${u.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => toggleUserStatus(u)}
                              disabled={busy}
                              className={`rounded-full h-8 px-3 ${u.status === "suspended" ? "border-primary/40 text-primary hover:bg-primary/10 hover:text-primary" : "border-accent/40 text-accent hover:bg-accent/10 hover:text-accent"}`}
                            >
                              {u.status === "suspended" ? <><UserCheck className="w-3.5 h-3.5 mr-1" /> Unblock</> : <><Ban className="w-3.5 h-3.5 mr-1" /> Suspend</>}
                            </Button>
                            <Button
                              data-testid={`delete-user-btn-${u.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteTarget(u)}
                              className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive h-8 px-3"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="adjustments" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <History className="w-4 h-4" /> Wallet ledger ({adjustments.length})
              </div>
              <div className="divide-y divide-border/40 max-h-[520px] overflow-auto">
                {adjustments.length === 0 && <div data-testid="no-adjustments" className="p-8 text-sm text-muted-foreground">No manual wallet adjustments yet.</div>}
                {adjustments.map((a) => (
                  <div key={a.id} data-testid={`adjustment-${a.id}`} className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{a.user_name} <span className="text-muted-foreground text-xs font-mono">· {a.user_email}</span></div>
                      {a.note && <div className="text-xs text-muted-foreground mt-1">Note: {a.note}</div>}
                      <div className="text-xs text-muted-foreground mt-1">By {a.admin_email} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                    <div className={`font-mono font-bold text-lg ${a.action === "credit" ? "text-primary" : "text-destructive"}`}>
                      {a.action === "credit" ? "+" : "−"}₹{a.amount.toFixed(2)}
                    </div>
                    <div className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
                      a.action === "credit" ? "border-primary/50 text-primary bg-primary/10" : "border-destructive/50 text-destructive bg-destructive/10"
                    }`}>{a.action}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="kyc" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <ShieldCheck className="w-4 h-4" /> KYC Submissions ({kycList.length})
              </div>
              <div className="divide-y divide-border/40 max-h-[600px] overflow-auto">
                {kycList.length === 0 && <div data-testid="no-kyc" className="p-8 text-sm text-muted-foreground">No KYC submissions yet.</div>}
                {kycList.map((k) => (
                  <div key={k.user_id} data-testid={`admin-kyc-${k.user_id}`} className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                      <div>
                        <div className="font-medium">{k.user_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{k.user_email}</div>
                        <div className="text-xs text-muted-foreground mt-1">Submitted {new Date(k.submitted_at).toLocaleString()}</div>
                      </div>
                      <div className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border flex items-center gap-1 ${
                        k.status === "approved" ? "border-primary/50 text-primary bg-primary/10"
                        : k.status === "rejected" ? "border-destructive/50 text-destructive bg-destructive/10"
                        : "border-accent/50 text-accent bg-accent/10"
                      }`}>
                        {k.status === "approved" ? <ShieldCheck className="w-3 h-3" /> : k.status === "rejected" ? <ShieldAlert className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {k.status}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-4">
                      <div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Aadhaar</div><div className="font-mono">{k.aadhaar}</div></div>
                      <div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">PAN</div><div className="font-mono">{k.pan}</div></div>
                      <div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Account Holder</div><div>{k.bank_holder_name}</div></div>
                      <div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Account #</div><div className="font-mono">{k.bank_account_number}</div></div>
                      <div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">IFSC</div><div className="font-mono">{k.bank_ifsc}</div></div>
                    </div>
                    {k.status === "pending" && (
                      <div className="flex flex-wrap gap-2 items-end">
                        <div className="flex-1 min-w-[200px]">
                          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Note (optional)</Label>
                          <Input data-testid={`kyc-note-${k.user_id}`} value={kycNote[k.user_id] || ""} onChange={(e) => setKycNote({ ...kycNote, [k.user_id]: e.target.value })} placeholder="Reason if rejecting" className="mt-1 h-10 bg-background border-border focus:border-primary focus:ring-primary text-sm" />
                        </div>
                        <Button data-testid={`kyc-approve-${k.user_id}`} size="sm" onClick={() => reviewKyc(k.user_id, "approve")} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10">
                          <ShieldCheck className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button data-testid={`kyc-reject-${k.user_id}`} size="sm" variant="destructive" onClick={() => reviewKyc(k.user_id, "reject")} disabled={busy} className="rounded-full h-10">
                          <ShieldAlert className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                    {k.status !== "pending" && k.note && (
                      <div className="text-xs text-muted-foreground mt-2">Note: {k.note}</div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border/60 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <Contact className="w-4 h-4" /> All customers ({adminCustomers.length})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    <tr className="border-b border-border/40">
                      <th className="text-left p-4">Customer</th>
                      <th className="text-left p-4">Mobile</th>
                      <th className="text-left p-4">Aadhaar</th>
                      <th className="text-left p-4">PAN</th>
                      <th className="text-left p-4">Added by</th>
                      <th className="text-left p-4">Added on</th>
                      <th className="text-right p-4">Files</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {adminCustomers.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-sm text-muted-foreground text-center" data-testid="no-admin-customers">No customers added yet.</td></tr>
                    )}
                    {adminCustomers.map((c) => (
                      <tr key={c.id} data-testid={`admin-customer-${c.id}`} className="hover:bg-white/[0.02]">
                        <td className="p-4">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">DOB {c.dob}</div>
                        </td>
                        <td className="p-4 font-mono text-xs">{c.mobile}</td>
                        <td className="p-4 font-mono text-xs">{c.aadhaar}</td>
                        <td className="p-4 font-mono text-xs">{c.pan}</td>
                        <td className="p-4">
                          <div className="font-medium">{c.added_by_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{c.added_by_email}</div>
                        </td>
                        <td className="p-4 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            <Button
                              data-testid={`admin-cust-edit-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setEditCust({ id: c.id, name: c.name, dob: c.dob, mobile: c.mobile, aadhaar: c.aadhaar, pan: c.pan, father_name: c.father_name || "", address: c.address || "", pin_code: c.pin_code || "", bank_account_number: c.bank_account_number || "", bank_ifsc: c.bank_ifsc || "" })}
                              className="rounded-full border-primary/40 text-primary hover:bg-primary/10 hover:text-primary h-8 px-3"
                            >
                              <Plus className="w-3.5 h-3.5 mr-1 rotate-45" /> Edit
                            </Button>
                            <Button
                              data-testid={`admin-cust-passbook-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                const res = await fetch(`${API_BASE}/user/customers/${c.id}/passbook`, { credentials: "include" });
                                if (!res.ok) { toast.error("Download failed"); return; }
                                const b = await res.blob();
                                const u = URL.createObjectURL(b);
                                const a = document.createElement("a");
                                a.href = u; a.download = c.passbook_filename || `passbook-${c.name}`;
                                document.body.appendChild(a); a.click(); a.remove();
                                URL.revokeObjectURL(u);
                              }}
                              className="rounded-full border-accent/40 text-accent hover:bg-accent/10 hover:text-accent h-8 px-3"
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> Passbook
                            </Button>
                            <Button
                              data-testid={`admin-cust-cert-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                const res = await fetch(`${API_BASE}/user/customers/${c.id}/certificate`, { credentials: "include" });
                                if (!res.ok) { toast.error("Download failed"); return; }
                                const b = await res.blob();
                                const u = URL.createObjectURL(b);
                                const a = document.createElement("a");
                                const safe = (c.name || "customer").replace(/[^A-Za-z0-9_-]/g, "");
                                a.href = u; a.download = `asiyanna-Certificate-${safe}.pdf`;
                                document.body.appendChild(a); a.click(); a.remove();
                                URL.revokeObjectURL(u);
                              }}
                              className="rounded-full border-primary/40 text-primary hover:bg-primary/10 hover:text-primary h-8 px-3"
                            >
                              <FileDown className="w-3.5 h-3.5 mr-1" /> Certificate
                            </Button>
                            <Button
                              data-testid={`admin-cust-delete-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteCustTarget(c)}
                              className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive h-8 px-3"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>


          <TabsContent value="tree" className="mt-6">
            <Card className="bg-card/60 border-border/70 rounded-2xl overflow-hidden">
              <div ref={treeRef} style={{ height: 560 }}>
                {tree && (
                  <Tree
                    data={tree}
                    orientation="vertical"
                    translate={translate}
                    pathFunc="step"
                    zoomable
                    collapsible
                    separation={{ siblings: 1.2, nonSiblings: 1.6 }}
                    nodeSize={{ x: 170, y: 90 }}
                  />
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card className="p-6 bg-card/60 border-border/70 rounded-2xl max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Commission Rates</div>
              <div className="font-display font-bold text-lg mb-4">Level-based percentages</div>
              <p className="text-sm text-muted-foreground mb-4">Comma-separated percentages, one per level. Currently: <span className="text-primary font-mono">{settings.rates.join(" / ")}%</span></p>
              <div className="flex gap-2">
                <Input data-testid="settings-rates-input" value={ratesInput} onChange={(e) => setRatesInput(e.target.value)} placeholder="10, 5, 3, 2, 1" className="h-12 bg-background border-border focus:border-primary focus:ring-primary" />
                <Button data-testid="settings-save-btn" onClick={saveSettings} disabled={busy} className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90">
                  Save
                </Button>
              </div>
              <div className="mt-6 pt-6 border-t border-border/40 text-sm">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Joining Fee</div>
                <div className="font-mono text-2xl text-primary">₹{settings.joining_fee.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-2">Fixed for now — edit in backend server.py</div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={!!adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)}>
          <DialogContent data-testid="adjust-wallet-dialog" className="bg-card border-border/70 rounded-2xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Adjust Wallet</DialogTitle>
              <DialogDescription>
                {adjustTarget && (
                  <span>
                    {adjustTarget.name} · <span className="font-mono text-primary">{adjustTarget.referral_code}</span>
                    <br />
                    Current balance: <span className="font-mono text-primary">₹{(adjustTarget.wallet_balance || 0).toFixed(2)}</span>
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="adjust-credit-toggle"
                  type="button"
                  onClick={() => setAdjustAction("credit")}
                  variant={adjustAction === "credit" ? "default" : "outline"}
                  className={`rounded-full ${adjustAction === "credit" ? "bg-primary text-primary-foreground" : "border-border"}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Credit
                </Button>
                <Button
                  data-testid="adjust-debit-toggle"
                  type="button"
                  onClick={() => setAdjustAction("debit")}
                  variant={adjustAction === "debit" ? "default" : "outline"}
                  className={`rounded-full ${adjustAction === "debit" ? "bg-destructive text-destructive-foreground" : "border-border"}`}
                >
                  <Minus className="w-4 h-4 mr-1" /> Debit
                </Button>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Amount (₹)</Label>
                <Input
                  data-testid="adjust-amount-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Note (optional)</Label>
                <Input
                  data-testid="adjust-note-input"
                  value={adjustNote}
                  onChange={(e) => setAdjustNote(e.target.value)}
                  placeholder="Reason for adjustment"
                  className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button data-testid="adjust-cancel-btn" variant="ghost" onClick={() => setAdjustTarget(null)} className="rounded-full">Cancel</Button>
              <Button
                data-testid="adjust-confirm-btn"
                onClick={submitAdjust}
                disabled={busy}
                className={`rounded-full ${adjustAction === "credit" ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}`}
              >
                {busy ? "Processing..." : `Confirm ${adjustAction === "credit" ? "Credit" : "Debit"}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-user-dialog" className="bg-card border-border/70 rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Delete this user?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget && (
                  <>
                    This will permanently remove <span className="text-foreground font-medium">{deleteTarget.name}</span> ({deleteTarget.email}), their KYC data and cancel any pending withdrawals.
                    Their commission history stays on the audit ledger. This action cannot be undone.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn" className="rounded-full">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-confirm-btn" onClick={confirmDelete} disabled={busy} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {busy ? "Deleting..." : "Delete permanently"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
          <DialogContent data-testid="approve-utr-dialog" className="bg-card border-border/70 rounded-2xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Approve withdrawal</DialogTitle>
              <DialogDescription>
                {approveTarget && (
                  <>
                    Confirm the bank transfer for <span className="text-foreground font-medium">{approveTarget.user_name}</span> —
                    <span className="text-primary font-mono"> ₹{approveTarget.amount?.toFixed(2)}</span>.
                    Enter the UTR reference from your bank once you've made the transfer.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">UTR / Transaction reference</Label>
                <Input
                  data-testid="approve-utr-input"
                  value={approveUtr}
                  onChange={(e) => setApproveUtr(e.target.value.toUpperCase())}
                  placeholder="e.g. NEFT124XXXXXX"
                  className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button data-testid="approve-utr-cancel" variant="ghost" onClick={() => setApproveTarget(null)} className="rounded-full">Cancel</Button>
              <Button data-testid="approve-utr-confirm" onClick={submitApprove} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? "Approving..." : "Approve & save UTR"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editCust} onOpenChange={(o) => !o && setEditCust(null)}>
          <DialogContent data-testid="edit-customer-dialog" className="bg-card border-border/70 rounded-2xl sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Edit customer</DialogTitle>
              <DialogDescription>Update customer identity fields. Passbook file cannot be changed here.</DialogDescription>
            </DialogHeader>
            {editCust && (
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Full name</Label>
                    <Input data-testid="edit-cust-name" value={editCust.name} onChange={(e) => setEditCust({ ...editCust, name: e.target.value })} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Date of birth</Label>
                    <Input data-testid="edit-cust-dob" type="date" value={editCust.dob} onChange={(e) => setEditCust({ ...editCust, dob: e.target.value })} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mobile</Label>
                    <Input data-testid="edit-cust-mobile" value={editCust.mobile} onChange={(e) => setEditCust({ ...editCust, mobile: e.target.value.replace(/\D/g, "") })} maxLength={15} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Aadhaar (12 digits)</Label>
                    <Input data-testid="edit-cust-aadhaar" value={editCust.aadhaar} onChange={(e) => setEditCust({ ...editCust, aadhaar: e.target.value.replace(/\D/g, "") })} maxLength={12} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">PAN</Label>
                    <Input data-testid="edit-cust-pan" value={editCust.pan} onChange={(e) => setEditCust({ ...editCust, pan: e.target.value.toUpperCase() })} maxLength={10} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Father's name</Label>
                    <Input data-testid="edit-cust-father" value={editCust.father_name || ""} onChange={(e) => setEditCust({ ...editCust, father_name: e.target.value })} maxLength={100} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pin code</Label>
                    <Input data-testid="edit-cust-pin" value={editCust.pin_code || ""} onChange={(e) => setEditCust({ ...editCust, pin_code: e.target.value.replace(/\D/g, "") })} maxLength={10} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Address</Label>
                    <Input data-testid="edit-cust-address" value={editCust.address || ""} onChange={(e) => setEditCust({ ...editCust, address: e.target.value })} maxLength={500} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bank account no.</Label>
                    <Input data-testid="edit-cust-bank-acc" value={editCust.bank_account_number || ""} onChange={(e) => setEditCust({ ...editCust, bank_account_number: e.target.value.replace(/\D/g, "") })} maxLength={25} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">IFSC code</Label>
                    <Input data-testid="edit-cust-ifsc" value={editCust.bank_ifsc || ""} onChange={(e) => setEditCust({ ...editCust, bank_ifsc: e.target.value.toUpperCase() })} maxLength={11} className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono uppercase" />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button data-testid="edit-cust-cancel" variant="ghost" onClick={() => setEditCust(null)} className="rounded-full">Cancel</Button>
              <Button data-testid="edit-cust-save" onClick={saveCustomer} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? "Saving..." : "Save changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteCustTarget} onOpenChange={(o) => !o && setDeleteCustTarget(null)}>
          <AlertDialogContent data-testid="delete-customer-dialog" className="bg-card border-border/70 rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Delete this customer?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteCustTarget && <>Permanently remove <span className="text-foreground font-medium">{deleteCustTarget.name}</span> and their passbook file. This cannot be undone.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cust-cancel" className="rounded-full">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-cust-confirm" onClick={confirmDeleteCustomer} disabled={busy} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {busy ? "Deleting..." : "Delete permanently"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent data-testid="add-customer-dialog" className="bg-card border-border/70 rounded-2xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Add new customer</DialogTitle>
              <DialogDescription>Create a member on their behalf. Optionally assign a sponsor to build the referral chain.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Full name</Label>
                <Input data-testid="new-user-name-input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Jane Doe" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                <Input data-testid="new-user-email-input" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Temporary password (min 6)</Label>
                <Input data-testid="new-user-password-input" type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Share with the customer" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Sponsor (choose upline)</Label>
                <Select value={newUser.sponsor_code} onValueChange={(v) => setNewUser({ ...newUser, sponsor_code: v })}>
                  <SelectTrigger data-testid="new-user-sponsor-select" className="mt-2 h-11 bg-background border-border focus:border-primary focus:ring-primary">
                    <SelectValue placeholder="Select sponsor" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-64">
                    <SelectItem value="none">— None (top-level) —</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.referral_code} data-testid={`sponsor-option-${u.referral_code}`}>
                        {u.name} · {u.referral_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Checkbox
                  id="mark-paid"
                  data-testid="new-user-mark-paid-checkbox"
                  checked={newUser.mark_paid}
                  onCheckedChange={(v) => setNewUser({ ...newUser, mark_paid: !!v })}
                  className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                />
                <Label htmlFor="mark-paid" className="text-sm cursor-pointer">
                  Mark joining fee as paid & distribute upline commissions
                </Label>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button data-testid="new-user-cancel-btn" variant="ghost" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
              <Button data-testid="new-user-create-btn" onClick={createUser} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? "Creating..." : "Create customer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
